import React, { useState, useEffect, useCallback, useRef } from 'react';
import UploadOneDrive from '../components/UploadOneDrive';
import { useParams, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import {
  formatDate, formatEuro, clienteNome, statoLabel, statoPillClass,
  Modal, ConfirmDialog, LoadingSpinner
} from '../components/shared';

// ─── COSTANTI ────────────────────────────────────────────────────────────────
const TIPI  = ['IVA','F24','730','IRES','IRAP','IMU','ALTRO'];


const STATI_OPTIONS = [
  { value:'APERTA',       label:'Aperta',       color:'var(--c-red)'   },
  { value:'PREPARATA',    label:'Preparata',    color:'var(--c-amber)' },
  { value:'INVIATA',      label:'Inviata',      color:'var(--c-green)' },
  { value:'SOSPESA',      label:'Sospesa',      color:'var(--text-faint)' },
  { value:'NON_PREVISTA', label:'Non prevista', color:'var(--text-faint)' },
  { value:'ALTRO',        label:'Altro',        color:'var(--c-blue)'  },
];

// ─── INPUT ANNO LIBERO ────────────────────────────────────────────────────────
function AnnoInput({ value, onChange, placeholder = 'Anno…', style = {} }) {
  return (
    <input
      type="number"
      min="1990" max="2099"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: 90, textAlign: 'center', fontFamily: 'monospace', ...style }}
      className="form-control"
    />
  );
}

const MODALITA_LABELS = {
  singolo: 'Cliente singolo',
  tutti:   'Tutti i clienti attivi',
  pf:      'Persone Fisiche',
  pg:      'Persone Giuridiche',
  selezione: 'Selezione clienti',
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function dataColor(data_scad, allInviata) {
  if (allInviata) return 'var(--c-green)';
  const diff = (new Date(data_scad) - new Date()) / 86400000;
  if (diff < 0)   return 'var(--c-red)';
  if (diff <= 7)  return 'var(--c-red)';
  if (diff <= 30) return 'var(--c-amber)';
  return 'var(--text-muted)';
}

function ProgressBar({ inviata, totale, nonAperte }) {
  // Considera avanzamento qualsiasi stato != APERTA
  const avanzati = nonAperte !== undefined ? nonAperte : inviata;
  const pct = totale > 0 ? Math.round((avanzati / totale) * 100) : 0;
  const color = pct === 100 ? 'var(--c-green)' : pct > 50 ? 'var(--c-amber)' : 'var(--accent)';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:5, background:'var(--border)', borderRadius:3, overflow:'hidden', minWidth:60 }}>
        <div style={{ width:`${pct}%`, height:'100%', background:color, transition:'width 0.3s' }} />
      </div>
      <span style={{ fontSize:11, color:'var(--text-faint)', whiteSpace:'nowrap' }}>{avanzati}/{totale}</span>
    </div>
  );
}


// ─── STATO SELECT CON NAVIGAZIONE TASTIERA ───────────────────────────────────
// Ogni select registra se stesso in un array globale per poter fare Tab
// tra tutti i dropdown della pagina con frecce + Enter
const selectRefs = { list: [], register(el) { if (el && !this.list.includes(el)) this.list.push(el); },
  unregister(el) { this.list = this.list.filter(r => r !== el); },
  next(el) { const i = this.list.indexOf(el); return this.list[i+1] || null; },
  prev(el) { const i = this.list.indexOf(el); return this.list[i-1] || null; },
};

function StatoSelect({ value, scadenzaId, onStato }) {
  const ref = React.useRef(null);
  const VALORI = STATI_OPTIONS.map(s => s.value);

  React.useEffect(() => {
    const el = ref.current;
    selectRefs.register(el);
    return () => selectRefs.unregister(el);
  }, []);

  const handleKeyDown = (e) => {
    const el = ref.current;
    if (!el) return;

    if (e.key === 'Tab') {
      e.preventDefault();
      const target = e.shiftKey ? selectRefs.prev(el) : selectRefs.next(el);
      if (target) { target.focus(); target.scrollIntoView({ block:'nearest', behavior:'smooth' }); }
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const idx = VALORI.indexOf(value);
      const next = VALORI[(idx + 1) % VALORI.length];
      onStato(scadenzaId, next);
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const idx = VALORI.indexOf(value);
      const prev = VALORI[(idx - 1 + VALORI.length) % VALORI.length];
      onStato(scadenzaId, prev);
      return;
    }

    // Scorciatoie tasto singolo: a=Aperta, p=Preparata, i=Inviata, s=Sospesa, n=Non prevista, l=aLtro
    const shortcuts = { a:'APERTA', p:'PREPARATA', i:'INVIATA', s:'SOSPESA', n:'NON_PREVISTA', l:'ALTRO' };
    if (shortcuts[e.key.toLowerCase()]) {
      e.preventDefault();
      onStato(scadenzaId, shortcuts[e.key.toLowerCase()]);
      // Avanza automaticamente al prossimo dopo shortcut
      setTimeout(() => {
        const target = selectRefs.next(el);
        if (target) { target.focus(); target.scrollIntoView({ block:'nearest', behavior:'smooth' }); }
      }, 80);
    }
  };

  const opt = STATI_OPTIONS.find(s => s.value === value);

  return (
    <div style={{ position:'relative' }}>
      <select
        ref={ref}
        value={value}
        onChange={e => onStato(scadenzaId, e.target.value)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        style={{
          padding:'4px 8px', fontSize:12, cursor:'pointer', width:'100%',
          borderRadius:'var(--radius-sm)',
          border:'1.5px solid var(--border-strong)',
          background:'var(--surface)',
          color: opt ? opt.color : 'var(--text)',
          fontWeight: 500,
          outline:'none',
          transition:'border-color 0.1s, box-shadow 0.1s',
        }}
        onFocus={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 3px rgba(29,95,170,0.15)'; }}
        onBlur={e  => { e.target.style.borderColor = 'var(--border-strong)'; e.target.style.boxShadow = 'none'; }}
      >
        {STATI_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </div>
  );
}

// ─── CONFIRM CON DIGITAZIONE ─────────────────────────────────────────────────
function ConfirmDigit({ titolo, n_clienti, onConfirm, onCancel }) {
  const [testo, setTesto] = useState('');
  const ok = testo.trim().toLowerCase() === 'conferma';
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth:460 }}>
        <div className="modal-header">
          <span className="modal-title" style={{ color:'var(--c-red)' }}>⚠ Elimina scadenza</span>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom:16, padding:'12px 16px', background:'#FCEAEA',
            borderRadius:'var(--radius-sm)', border:'1px solid #F7C1C1' }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{titolo}</div>
            <div style={{ fontSize:13, color:'var(--c-red)' }}>
              Verranno eliminate <strong>{n_clienti}</strong> posizioni cliente. Operazione irreversibile.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ marginBottom:8 }}>
              Digita <strong>CONFERMA</strong> per procedere
            </label>
            <input className="form-control" value={testo} onChange={e => setTesto(e.target.value)}
              placeholder="CONFERMA" autoFocus
              style={{ fontFamily:'monospace', letterSpacing:'2px',
                borderColor: ok ? 'var(--c-green)' : testo ? 'var(--c-red)' : undefined }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onCancel}>Annulla</button>
          <button className="btn" disabled={!ok} onClick={onConfirm}
            style={{ background:ok?'var(--c-red)':'var(--bg)', color:ok?'white':'var(--text-faint)',
              borderColor:ok?'var(--c-red)':'var(--border)', cursor:ok?'pointer':'not-allowed', transition:'all 0.15s' }}>
            Elimina tutto
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FORM NUOVA SCADENZA ─────────────────────────────────────────────────────
function ScadenzaForm({ initial = {}, clientiList, onSave, onClose, loading }) {
  const isEdit = !!initial.id;
  const annoCorrente = new Date().getFullYear().toString();
  const [d, setD] = useState({
    id_cliente:'', tipo:'IVA', descrizione:'', data_scad:'',
    anno: annoCorrente,
    importo:'', stato:'APERTA', note:'', modalita_bulk:'singolo',
    ...initial, id_cliente:initial.id_cliente||'', importo:initial.importo||'',
    anno: initial.anno || annoCorrente,
    modalita_bulk:initial.modalita_bulk||'singolo',
  });
  const set = (k, v) => setD(p => ({ ...p, [k]:v }));
  const isBulk = d.modalita_bulk !== 'singolo';
  const isSelezione = d.modalita_bulk === 'selezione';
  const [clientiSelezionati, setClientiSelezionati] = useState([]);
  const counts = {
    tutti:     clientiList.length,
    pf:        clientiList.filter(c => c.persona_fisica).length,
    pg:        clientiList.filter(c => c.societa).length,
    selezione: clientiSelezionati.length,
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(isSelezione ? { ...d, clienti_ids: clientiSelezionati } : d); }}>
      {!isEdit && (
        <div style={{ marginBottom:20 }}>
          <div className="form-label" style={{ marginBottom:10 }}>Destinatari</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:6 }}>
            {Object.entries(MODALITA_LABELS).map(([key, label]) => {
              const count = key==='singolo' ? null : counts[key];
              const active = d.modalita_bulk===key;
              return (
                <button key={key} type="button" onClick={() => set('modalita_bulk', key)}
                  style={{ padding:'9px 12px', borderRadius:'var(--radius-sm)', textAlign:'left',
                    border:`2px solid ${active?'var(--accent)':'var(--border-strong)'}`,
                    background:active?'var(--accent-light)':'var(--surface)', cursor:'pointer' }}>
                  <div style={{ fontSize:12.5, fontWeight:500, color:active?'var(--accent)':'var(--text)' }}>{label}</div>
                  {count!=null && <div style={{ fontSize:11, color:'var(--text-faint)' }}>{count} clienti</div>}
                </button>
              );
            })}
          </div>
          {isBulk && !isSelezione && (
            <div style={{ marginTop:8, padding:'8px 12px', background:'#FDF0DC',
              border:'1px solid #FAC775', borderRadius:'var(--radius-sm)', fontSize:12, color:'var(--c-amber)' }}>
              ⚠ Verranno create <strong>{counts[d.modalita_bulk]}</strong> scadenze separate
            </div>
          )}
          {isSelezione && (
            <div style={{ marginTop:8 }}>
              <div style={{ fontSize:11, color:'var(--text-faint)', marginBottom:6 }}>
                {clientiSelezionati.length} clienti selezionati
              </div>
              <div style={{ maxHeight:200, overflowY:'auto', border:'1px solid var(--border)',
                borderRadius:'var(--radius-sm)', padding:'4px 0' }}>
                {clientiList.map(cli => (
                  <label key={cli.id} style={{ display:'flex', alignItems:'center', gap:8,
                    padding:'5px 12px', cursor:'pointer', fontSize:13,
                    background: clientiSelezionati.includes(cli.id) ? 'var(--accent-light)' : 'transparent' }}>
                    <input type="checkbox"
                      checked={clientiSelezionati.includes(cli.id)}
                      onChange={e => {
                        setClientiSelezionati(p =>
                          e.target.checked ? [...p, cli.id] : p.filter(id => id !== cli.id)
                        );
                      }} />
                    <span>{clienteNome(cli)}</span>
                    {cli.codice && <span style={{ fontSize:11, color:'var(--text-faint)' }}>({cli.codice})</span>}
                  </label>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:6 }}>
                <button type="button" className="btn btn-sm"
                  onClick={() => setClientiSelezionati(clientiList.map(c => c.id))}>Tutti</button>
                <button type="button" className="btn btn-sm"
                  onClick={() => setClientiSelezionati([])}>Nessuno</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="form-grid">
        {!isBulk && (
          <div className="form-group form-full">
            <label className="form-label">Cliente</label>
            <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
              <option value="">— Nessun cliente —</option>
              {clientiList.map(c => <option key={c.id} value={c.id}>{clienteNome(c)} ({c.codice})</option>)}
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Tipo *</label>
          <select className="form-control" value={d.tipo} onChange={e => set('tipo', e.target.value)} required>
            {TIPI.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Anno *</label>
          <input className="form-control" type="number" min="2000" max="2099"
            value={d.anno} onChange={e => set('anno', e.target.value)}
            required style={{ fontFamily:'monospace' }} />
        </div>
        <div className="form-group">
          <label className="form-label">Data Scadenza *</label>
          <input className="form-control" type="date" value={d.data_scad} onChange={e => set('data_scad', e.target.value)} required />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Descrizione *</label>
          <input className="form-control" value={d.descrizione} onChange={e => set('descrizione', e.target.value)} required placeholder="Es. IVA trimestrale I trim 2026" />
        </div>
        <div className="form-group">
          <label className="form-label">Importo (€)</label>
          <input className="form-control" type="number" step="0.01" min="0" value={d.importo} onChange={e => set('importo', e.target.value)} placeholder="0,00" />
        </div>
        <div className="form-group">
          <label className="form-label">Stato iniziale</label>
          <select className="form-control" value={d.stato} onChange={e => set('stato', e.target.value)}>
            {STATI_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note} onChange={e => set('note', e.target.value)} rows={2} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding:'16px 0 0', borderTop:'1px solid var(--border)', marginTop:16 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Salvataggio…' : isSelezione ? `Crea ${clientiSelezionati.length} scadenze` : isBulk ? `Crea ${counts[d.modalita_bulk]} scadenze` : 'Salva'}
        </button>
      </div>
    </form>
  );
}

// ─── EDIT SINGOLA POSIZIONE ───────────────────────────────────────────────────
function EditClienteForm({ scadenza, onSave, onClose, loading }) {
  const [stato, setStato]     = useState(scadenza.stato);
  const [importo, setImporto] = useState(scadenza.importo || '');
  const [note, setNote]       = useState(scadenza.note || '');
  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ stato, importo, note }); }}>
      <div style={{ marginBottom:12, padding:'10px 14px', background:'var(--accent-light)',
        borderRadius:'var(--radius-sm)', fontSize:13, fontWeight:500, color:'var(--accent)' }}>
        {clienteNome(scadenza)}
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Stato lavorazione</label>
          <select className="form-control" value={stato} onChange={e => setStato(e.target.value)}>
            {STATI_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Importo (€)</label>
          <input className="form-control" type="number" step="0.01" value={importo} onChange={e => setImporto(e.target.value)} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={note} onChange={e => setNote(e.target.value)} rows={3} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding:'16px 0 0', borderTop:'1px solid var(--border)', marginTop:16 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Salvataggio…':'Salva'}</button>
      </div>
    </form>
  );
}

// ─── CARD SCADENZA ────────────────────────────────────────────────────────────
function ScadenzaRow({ gruppo, navigate, past, onDelete }) {
  const allInviata = gruppo.n_inviata === gruppo.n_clienti && gruppo.n_clienti > 0;
  const dc = dataColor(gruppo.data_scad, allInviata);
  const diff = Math.round((new Date(gruppo.data_scad) - new Date()) / 86400000);
  const diffLabel = isNaN(diff) ? '' : diff < 0 ? `${Math.abs(diff)}gg fa` : diff === 0 ? 'Oggi' : `${diff}gg`;

  return (
    <div style={{
      display:'grid', gridTemplateColumns:'100px 210px 58px 58px 58px 58px 58px 1fr',
      gap:6, padding:'8px 12px', alignItems:'center',
      borderBottom:'1px solid var(--border)',
      background:'var(--surface)',
      cursor:'pointer',
      transition:'background 0.1s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
    onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
    onClick={() => navigate('/scadenze/' + encodeURIComponent(gruppo.tipo) + '/' + encodeURIComponent(gruppo.descrizione||'') + '/' + (gruppo.data_scad||''))}>
      {/* Data */}
      <div>
        <div style={{ fontSize:13, fontWeight:700, color: dc.color }}>{formatDate(gruppo.data_scad)}</div>
        {diffLabel && <div style={{ fontSize:10, color: dc.color, opacity:0.8 }}>{diffLabel}</div>}
      </div>
      {/* Descrizione */}
      <div>
        <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{gruppo.descrizione||gruppo.tipo}</div>
        <div style={{ fontSize:11, color:'var(--text-faint)' }}>{gruppo.tipo} · {gruppo.n_clienti} clienti</div>
      </div>
      {/* Contatori */}
      {[
        [gruppo.n_aperte||0, 'var(--c-red)'],
        [gruppo.n_preparata||0, 'var(--c-amber)'],
        [gruppo.n_inviata||0, 'var(--c-green)'],
        [gruppo.n_sospesa||0, 'var(--text-faint)'],
        [gruppo.n_non_prevista||0, 'var(--text-faint)'],
      ].map(([v, col], i) => (
        <div key={i} style={{ textAlign:'center', fontWeight:700, fontSize:14, color: v > 0 ? col : 'var(--border-strong)' }}>{v}</div>
      ))}
      {/* Progress + apri */}
      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
        <ProgressBar inviata={gruppo.n_inviata} totale={gruppo.n_clienti} nonAperte={gruppo.n_clienti - (gruppo.n_aperte||0)} />
        <button className="btn btn-sm btn-primary" style={{ padding:'4px 10px', fontSize:12, flexShrink:0 }}
          onClick={e => { e.stopPropagation(); navigate('/scadenze/' + encodeURIComponent(gruppo.tipo) + '/' + encodeURIComponent(gruppo.descrizione||'') + '/' + (gruppo.data_scad||'')); }}>
          Apri
        </button>
        {onDelete && (
          <button className="btn btn-sm btn-danger btn-icon" style={{ flexShrink:0 }} title="Elimina scadenza"
            onClick={e => { e.stopPropagation(); onDelete(gruppo); }}>🗑</button>
        )}
      </div>
    </div>
  );
}


function exportExcel(gruppi, filtri, singoloGruppo) {
  // If singoloGruppo is provided, export only that group
  const dati = singoloGruppo ? [singoloGruppo] : gruppi;
  // Build CSV (semicolon-separated, Italian Excel style) then save as .csv
  const rows = [];
  rows.push(['SCADENZA','TIPO','DATA SCADENZA','N. CLIENTI','CLIENTE','CODICE','STATO','IMPORTO','NOTE']);
  for (const g of gruppi) {
    if (g.clienti.length === 0) {
      rows.push([g.descrizione||g.tipo, g.tipo, g.data_scad, g.n_clienti, '', '', '', g.importo||'', '']);
    } else {
      for (const c of g.clienti) {
        rows.push([
          g.descrizione||g.tipo,
          g.tipo,
          g.data_scad,
          g.n_clienti,
          clienteNome(c),
          c.codice||'',
          statoLabel(c.stato),
          c.importo||'',
          c.note||'',
        ]);
      }
    }
  }
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(';')).join('\r\n');
  const bom = '\uFEFF'; // UTF-8 BOM for Excel
  const blob = new Blob([bom + csv], { type:'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `scadenze_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── PRINT ───────────────────────────────────────────────────────────────────
function printScadenze(gruppi) {
  const now = new Date().toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric' });

  const rows = gruppi.flatMap(g =>
    g.clienti.length === 0
      ? [{ scadenza: g.descrizione||g.tipo, tipo:g.tipo, data:g.data_scad, cliente:'—', codice:'', stato:'—', importo:'', note:'' }]
      : g.clienti.map(c => ({ scadenza:g.descrizione||g.tipo, tipo:g.tipo, data:g.data_scad, cliente:clienteNome(c), codice:c.codice||'', stato:statoLabel(c.stato), importo:c.importo?`€ ${Number(c.importo).toLocaleString('it-IT',{minimumFractionDigits:2})}`:'', note:c.note||'' }))
  );

  const html = `<!DOCTYPE html><html lang="it"><head><meta charset="UTF-8">
  <title>Scadenze — Studio SP</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #1a1a1a; padding: 20px; }
    h1 { font-size: 16px; margin-bottom: 4px; }
    .meta { font-size: 10px; color: #666; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1D5FAA; color: white; padding: 6px 8px; text-align: left; font-size: 10px; }
    td { padding: 5px 8px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
    tr:nth-child(even) td { background: #f5f8ff; }
    .scad-name { font-weight: bold; color: #1D5FAA; }
    @media print { body { padding: 0; } }
  </style></head><body>
  <h1>Scadenze Studio SP</h1>
  <div class="meta">Stampato il ${now} — ${rows.length} posizioni</div>
  <table>
    <thead><tr><th>Scadenza</th><th>Tipo</th><th>Data</th><th>Cliente</th><th>Stato</th><th>Importo</th><th>Note</th></tr></thead>
    <tbody>
      ${rows.map(r => `<tr>
        <td class="scad-name">${r.scadenza}</td>
        <td>${r.tipo}</td>
        <td style="white-space:nowrap">${new Date(r.data).toLocaleDateString('it-IT')}</td>
        <td>${r.cliente}${r.codice?` <span style="color:#888;font-size:9px">(${r.codice})</span>`:''}</td>
        <td>${r.stato}</td>
        <td style="white-space:nowrap;font-family:monospace">${r.importo}</td>
        <td style="color:#555;font-size:10px">${r.note}</td>
      </tr>`).join('')}
    </tbody>
  </table>
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.onload = () => { w.print(); };
}


// ─── ALLEGATI SCADENZA ────────────────────────────────────────────────────────
function AllegatiScadenza({ scadenzaId, locked }) {
  const [allegati, setAllegati] = React.useState([]);
  const [loading, setLoading]   = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    fetch(`/api/scadenze/${scadenzaId}/allegati`)
      .then(r => r.json())
      .then(j => { if (j.success) setAllegati(j.data || []); })
      .finally(() => setLoading(false));
  }, [scadenzaId]);

  React.useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    await fetch(`/api/scadenze/${scadenzaId}/allegati/${id}`, { method:'DELETE' });
    load();
  };

  const ext = (nome) => nome.split('.').pop().toLowerCase();
  const iconaFile = (nome) => {
    const e = ext(nome);
    if (['pdf'].includes(e)) return '📄';
    if (['xls','xlsx'].includes(e)) return '📊';
    if (['doc','docx'].includes(e)) return '📝';
    if (['jpg','jpeg','png','gif'].includes(e)) return '🖼';
    return '📎';
  };

  return (
    <div style={{ marginTop:12, borderTop:'1px solid var(--border)', paddingTop:10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <span style={{ fontSize:11, fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.4px' }}>
          📎 Allegati
        </span>
        {!locked && (
          <UploadOneDrive
            endpoint={`/api/scadenze/${scadenzaId}/allegati`}
            onUpload={load}
            addToast={() => {}}
            label="+ Allega"
          />
        )}
      </div>
      {loading ? <div style={{ fontSize:12, color:'var(--text-faint)' }}>⏳</div>
      : allegati.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--text-faint)', fontStyle:'italic' }}>Nessun allegato</div>
      ) : (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {allegati.map(a => (
            <div key={a.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px',
              background:'var(--surface)', border:'1px solid var(--border)', borderRadius:6 }}>
              <span style={{ fontSize:14 }}>{iconaFile(a.nome || a.filename || '')}</span>
              <a href={a.url || `/uploads/${a.filename}`} target="_blank" rel="noreferrer"
                style={{ fontSize:12, color:'var(--accent)', textDecoration:'none', maxWidth:160,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                title={a.nome || a.filename}>
                {(a.nome || a.filename || '').replace(/^\d+_/, '')}
              </a>
              {!locked && (
                <button onClick={() => handleDelete(a.id)}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--c-red)', fontSize:14, lineHeight:1, padding:'0 2px' }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── FORM NUOVO CLIENTE NON IN ANAGRAFICA ────────────────────────────────────
function NuovoClienteScadenzaForm({ onSave, onClose, loading }) {
  const [nome, setNome]     = useState('');
  const [codice, setCodice] = useState('');
  const [note, setNote]     = useState('');
  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ nome, codice, note }); }}>
      <div className="form-grid">
        <div className="form-group form-full">
          <label className="form-label">Nome / Denominazione *</label>
          <input className="form-control" value={nome} onChange={e => setNome(e.target.value)} required
            placeholder="Es. Mario Rossi" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Codice fiscale / P.IVA</label>
          <input className="form-control" value={codice} onChange={e => setCodice(e.target.value)}
            placeholder="RSSMRA80A01H501U" style={{ fontFamily:'monospace' }} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={note} onChange={e => setNote(e.target.value)} rows={2} />
        </div>
      </div>
      <div style={{ fontSize:11, color:'var(--text-faint)', marginBottom:12, padding:'8px 12px',
        background:'var(--bg)', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)' }}>
        ℹ Il cliente verrà creato in anagrafica e aggiunto automaticamente a questa scadenza.
      </div>
      <div className="modal-footer" style={{ padding:'12px 0 0', borderTop:'1px solid var(--border)' }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? '⏳ Creazione…' : '+ Crea e aggiungi'}
        </button>
      </div>
    </form>
  );
}

// ─── DETTAGLIO SCADENZA (pagina separata) ────────────────────────────────────
function ClienteRiga({ c: sc, onStato, onSave, onDelete, expanded, onToggle, locked, tabIndex }) {
  const [editing, setEditing] = React.useState(false);
  const [importo, setImporto] = React.useState(sc.importo || '');
  const [note, setNote]       = React.useState(sc.note || '');
  const stato = STATI_OPTIONS.find(s => s.value === sc.stato) || STATI_OPTIONS[0];

  const handleSave = async () => {
    await onSave(sc.id, { importo, note, stato: sc.stato });
    setEditing(false);
  };

  // Aggiorna campi locali quando cambia sc
  React.useEffect(() => { setImporto(sc.importo || ''); setNote(sc.note || ''); }, [sc.importo, sc.note]);

  const gridCols = '24px minmax(160px,260px) 160px 100px 1fr 64px';

  return (
    <>
      {/* Riga testata */}
      <div style={{
        display:'grid', gridTemplateColumns: gridCols,
        gap:6, padding:'7px 14px', borderBottom:'1px solid var(--border)',
        background: expanded ? 'var(--accent-light)' : 'var(--surface)',
        alignItems:'center',
      }}>
        {/* Toggle */}
        <div style={{ fontSize:11, color:'var(--text-faint)', textAlign:'center', cursor:'pointer' }}
          onClick={onToggle}>{expanded ? '▾' : '▸'}</div>
        {/* Cliente */}
        <div style={{ fontWeight:500, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', cursor:'pointer' }}
          title={clienteNome(sc)} onClick={onToggle}>{clienteNome(sc)}</div>
        {/* Stato — modificabile direttamente in testata */}
        <div onClick={e => e.stopPropagation()}>
          {locked ? (
            <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600,
              background: stato.color + '22', color: stato.color }}>{stato.label}</span>
          ) : (
            <StatoSelect value={sc.stato} scadenzaId={sc.id} onStato={onStato} tabIndex={tabIndex} />
          )}
        </div>
        {/* Importo */}
        <div style={{ fontSize:12, fontFamily:'monospace', color:'var(--text-muted)', cursor:'pointer' }}
          onClick={onToggle}>{sc.importo ? formatEuro(sc.importo) : '—'}</div>
        {/* Note preview */}
        <div style={{ fontSize:12, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', cursor:'pointer' }}
          title={sc.note} onClick={onToggle}>{sc.note || '—'}</div>
        {/* Azioni */}
        <div style={{ display:'flex', gap:3 }} onClick={e => e.stopPropagation()}>
          {!locked && <button className="btn btn-sm btn-icon" title="Modifica dettagli"
            onClick={() => { setEditing(!editing); if (!expanded) onToggle(); }}>✎</button>}
          <button className="btn btn-sm btn-danger btn-icon" onClick={() => onDelete(sc)}>×</button>
        </div>
      </div>

      {/* Dettaglio espanso */}
      {expanded && (
        <div style={{ padding:'12px 44px 14px', background:'var(--bg)', borderBottom:'1px solid var(--border)' }}>
          {editing && !locked ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, maxWidth:560 }}>
              <div>
                <div style={{ fontSize:11, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:4 }}>Importo (€)</div>
                <input className="form-control" type="number" value={importo}
                  onChange={e => setImporto(e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <div style={{ fontSize:11, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:4 }}>Codice cliente</div>
                <div style={{ fontSize:14, padding:'8px 0', color:'var(--text-muted)' }}>{sc.codice || '—'}</div>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <div style={{ fontSize:11, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:4 }}>Note</div>
                <textarea className="form-control" rows={3} value={note}
                  onChange={e => setNote(e.target.value)} placeholder="Note…" />
              </div>
              <div style={{ gridColumn:'1/-1', display:'flex', gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave}>✓ Salva</button>
                <button className="btn btn-sm" onClick={() => { setEditing(false); setImporto(sc.importo||''); setNote(sc.note||''); }}>Annulla</button>
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'auto auto auto 1fr', gap:24, alignItems:'start' }}>
              <div>
                <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Codice</div>
                <div style={{ fontSize:13 }}>{sc.codice || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Importo</div>
                <div style={{ fontSize:13, fontFamily:'monospace' }}>{sc.importo ? formatEuro(sc.importo) : '—'}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Stato</div>
                <div style={{ fontSize:13, color: stato.color, fontWeight:600 }}>{stato.label}</div>
              </div>
              {sc.note && (
                <div>
                  <div style={{ fontSize:10, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:2 }}>Note</div>
                  <div style={{ fontSize:13, lineHeight:1.5, whiteSpace:'pre-wrap', color:'var(--text)' }}>{sc.note}</div>
                </div>
              )}
            </div>
          )}
          {/* Allegati */}
          <AllegatiScadenza scadenzaId={sc.id} locked={locked} />
        </div>
      )}
    </>
  );
}

export function ScadenzaDettaglio({ addToast }) {
  const { tipo, descrizione, data } = useParams();
  const navigate = useNavigate();
  const [gruppo, setGruppo]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [statiF, setStatiF]   = useState([]);
  const [q, setQ]             = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [locked, setLocked]   = useState(false);
  const [selected, setSelected] = useState(new Set()); // selezione massiva
  const [showNuovoCliente, setShowNuovoCliente] = useState(false);
  const [savingNuovo, setSavingNuovo] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.getScadenzeRaggruppate({ tipo, descrizione, data_scad: data }).then(gruppi => {
      const g = gruppi.find(g =>
        g.tipo === tipo &&
        (g.descrizione||'') === (descrizione||'') &&
        g.data_scad === data
      );
      setGruppo(g || null);
    }).finally(() => setLoading(false));
  }, [tipo, descrizione, data]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand  = (id) => setExpanded(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll     = () => { if (gruppo) setExpanded(new Set(gruppo.clienti.map(c => c.id))); };
  const collapseAll   = () => setExpanded(new Set());

  const handleStato = async (scadenzaId, stato) => {
    try {
      await api.patchStato(scadenzaId, stato);
      setGruppo(p => p ? ({
        ...p,
        clienti: p.clienti.map(c => c.id===scadenzaId ? {...c, stato} : c),
        n_inviata:      p.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='INVIATA').length,
        n_preparata:    p.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='PREPARATA').length,
        n_aperte:       p.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='APERTA').length,
        n_sospesa:      p.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='SOSPESA').length,
        n_non_prevista: p.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='NON_PREVISTA').length,
      }) : p);
    } catch(e) { addToast(e.message, 'error'); }
  };

  const handleSaveCliente = async (scadenzaId, { importo, note }) => {
    setSaving(true);
    try {
      const scad = await api.getScadenza(scadenzaId);
      await api.updateScadenza(scadenzaId, { ...scad, importo, note });
      addToast('Salvato', 'success');
      setGruppo(p => p ? ({
        ...p, clienti: p.clienti.map(c => c.id===scadenzaId ? {...c, importo, note} : c)
      }) : p);
    } catch(e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll   = () => setSelected(new Set(clientiFiltrati.map(c => c.id)));
  const clearSelect = () => setSelected(new Set());

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Eliminare ${selected.size} posizioni selezionate?`)) return;
    try {
      await Promise.all([...selected].map(id => api.deleteScadenza(id)));
      addToast(`${selected.size} posizioni eliminate`, 'success');
      setSelected(new Set()); load();
    } catch(e) { addToast(e.message, 'error'); }
  };

  const handleNuovoCliente = async ({ nome, codice, note }) => {
    setSavingNuovo(true);
    try {
      // Crea cliente in anagrafica
      const nuovoCliente = await api.createCliente({
        nome, cognome: '', codice: codice || '', persona_fisica: 1,
        stato: 'ATTIVO', note: note || '',
      });
      // Crea scadenza per questo cliente con gli stessi dati del gruppo
      await api.createScadenza({
        id_cliente: nuovoCliente.id,
        tipo, descrizione, data_scad: data,
        stato: 'APERTA',
      });
      addToast('Cliente creato e aggiunto alla scadenza', 'success');
      setShowNuovoCliente(false); load();
    } catch(e) { addToast('Errore: ' + e.message, 'error'); }
    finally { setSavingNuovo(false); }
  };

  const handleDelete = async () => {
    try {
      await api.deleteScadenza(delItem.id);
      addToast('Eliminato', 'success');
      setDelItem(null); load();
    } catch(e) { addToast(e.message, 'error'); }
  };

  const toggleStato = (val) => setStatiF(p => p.includes(val) ? p.filter(s=>s!==val) : [...p, val]);

  // Contatori aggiornati in tempo reale
  const conteggioStati = React.useMemo(() => {
    if (!gruppo) return {};
    return STATI_OPTIONS.reduce((acc, s) => {
      acc[s.value] = gruppo.clienti.filter(c => c.stato === s.value).length;
      return acc;
    }, {});
  }, [gruppo]);

  const clientiFiltrati = gruppo ? gruppo.clienti.filter(c => {
    if (statiF.length && !statiF.includes(c.stato)) return false;
    if (q && !clienteNome(c).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }) : [];

  const handlePrint = () => printScadenze(gruppo ? [{ ...gruppo, clienti: clientiFiltrati }] : []);

  if (loading) return <div className="page-content"><LoadingSpinner /></div>;
  if (!gruppo) return (
    <div className="page-content">
      <div className="card"><div className="empty-state">Scadenza non trovata</div></div>
    </div>
  );

  return (
    <div className="page-content">

      {/* ── Header compatto ── */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <button className="btn btn-sm" onClick={() => navigate('/scadenze')}>← Scadenze</button>
        <div style={{ flex:1, minWidth:0 }}>
          <span style={{ fontSize:17, fontWeight:700, color:'var(--text)' }}>{gruppo.descrizione || gruppo.tipo}</span>
          <span style={{ fontSize:13, color:'var(--text-faint)', marginLeft:10 }}>
            {gruppo.tipo} · {formatDate(gruppo.data_scad)} · {gruppo.n_clienti} clienti
          </span>
        </div>
        {/* Azioni */}
        <div style={{ display:'flex', gap:6, flexShrink:0 }}>
          <button className="btn btn-sm" onClick={expandAll} title="Espandi tutti">⊞</button>
          <button className="btn btn-sm" onClick={collapseAll} title="Comprimi tutti">⊟</button>
          <button className="btn btn-sm" onClick={handlePrint}>🖨</button>
          <button className="btn btn-sm" onClick={() => exportExcel([{ ...gruppo, clienti: clientiFiltrati }], {}, gruppo)}>📊</button>
          {!locked && selected.size > 0 && (
            <button className="btn btn-sm btn-danger" onClick={handleDeleteSelected}>
              🗑 Elimina {selected.size} selezionati
            </button>
          )}
          {!locked && (
            <button className="btn btn-sm btn-primary" onClick={() => setShowNuovoCliente(true)}>
              + Nuovo cliente
            </button>
          )}
          {locked ? (
            <button className="btn btn-sm btn-primary" onClick={() => setLocked(false)}>🔓 Apri scadenza</button>
          ) : (
            <button className="btn btn-sm" style={{ borderColor:'var(--c-amber)', color:'var(--c-amber)' }}
              onClick={() => setLocked(true)}>🔒 Chiudi scadenza</button>
          )}
        </div>
      </div>

      {/* ── Filtri + stati con contatori ── */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:12,
        background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 14px' }}>
        <input className="form-control" placeholder="Cerca cliente…" value={q} onChange={e => setQ(e.target.value)}
          style={{ width:180, flexShrink:0 }} />
        {STATI_OPTIONS.map(s => {
          const active = statiF.includes(s.value);
          const count  = conteggioStati[s.value] || 0;
          return (
            <button key={s.value} type="button" onClick={() => toggleStato(s.value)}
              style={{ padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer',
                border:`1.5px solid ${active ? s.color : 'var(--border-strong)'}`,
                background: active ? s.color : 'transparent',
                color: active ? 'white' : 'var(--text-muted)',
                display:'flex', alignItems:'center', gap:5 }}>
              {s.label}
              <span style={{ background: active ? 'rgba(255,255,255,0.3)' : s.color + '22',
                color: active ? 'white' : s.color,
                borderRadius:10, padding:'0 5px', fontSize:10, fontWeight:700 }}>{count}</span>
            </button>
          );
        })}
        {(q || statiF.length > 0) && (
          <button className="btn btn-sm" style={{ color:'var(--c-red)' }} onClick={() => { setQ(''); setStatiF([]); }}>✕</button>
        )}
        {locked && <span style={{ marginLeft:'auto', fontSize:12, color:'var(--c-amber)', fontWeight:600 }}>🔒 Scadenza chiusa — sola lettura</span>}
      </div>

      {/* ── Tabella clienti ── */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
        {/* Intestazione colonne */}
        <div style={{ display:'grid', gridTemplateColumns:'28px 24px minmax(160px,260px) 160px 100px 1fr 64px',
          gap:6, padding:'7px 14px', background:'var(--bg)',
          borderBottom:'2px solid var(--border)',
          fontSize:10.5, fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.4px' }}>
          <div style={{ textAlign:'center' }}>
            <input type="checkbox" onChange={e => e.target.checked ? selectAll() : clearSelect()}
              checked={selected.size > 0 && selected.size === clientiFiltrati.length} />
          </div>
          <div></div>
          <div>Cliente</div>
          <div>Stato</div>
          <div>Importo</div>
          <div>Note</div>
          <div></div>
        </div>
        {clientiFiltrati.length === 0 ? (
          <div className="empty-state" style={{ padding:24 }}>Nessun cliente trovato</div>
        ) : clientiFiltrati.map((sc, idx) => (
          <div key={sc.id} style={{ display:'flex', alignItems:'flex-start' }}>
            <div style={{ width:28, paddingTop:10, paddingLeft:10, flexShrink:0 }}>
              {!locked && <input type="checkbox" checked={selected.has(sc.id)}
                onChange={() => toggleSelect(sc.id)} />}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <ClienteRiga c={sc}
                onStato={handleStato}
                onSave={handleSaveCliente}
                onDelete={setDelItem}
                expanded={expanded.has(sc.id)}
                onToggle={() => toggleExpand(sc.id)}
                locked={locked}
                tabIndex={idx + 1} />
            </div>
          </div>
        ))}
      </div>

      {delItem && (
        <ConfirmDialog message={`Eliminare la scadenza di "${clienteNome(delItem)}"?`}
          onConfirm={handleDelete} onCancel={() => setDelItem(null)} />
      )}
      {showNuovoCliente && (
        <Modal title="Aggiungi nuovo cliente" onClose={() => setShowNuovoCliente(false)}>
          <NuovoClienteScadenzaForm
            onSave={handleNuovoCliente}
            onClose={() => setShowNuovoCliente(false)}
            loading={savingNuovo} />
        </Modal>
      )}
    </div>
  );
}



export default function Scadenze({ addToast }) {
  const navigate = useNavigate();
  const [gruppi, setGruppi]       = useState([]);
  const [clienti, setClienti]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState('');
  const [tipoF, setTipoF]         = useState('');
  const [annoF, setAnnoF]         = useState('');
  const [statiF, setStatiF]       = useState([]); // array di stati selezionati
  const [showForm, setShowForm]   = useState(false);
  const [editCliente, setEditCliente] = useState(null);
  const [delItem, setDelItem]     = useState(null);
  const [delGruppo, setDelGruppo] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [showNuovoCliente, setShowNuovoCliente] = useState(false);
  const [savingNuovo, setSavingNuovo] = useState(false);
  const [searchParams]            = useSearchParams();

  useEffect(() => { if (searchParams.get('new')==='1') setShowForm(true); }, [searchParams]);
  useEffect(() => { api.getClienti({ limit:300 }).then(d => setClienti(d.rows)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q)            params.q    = q;
    if (tipoF)        params.tipo = tipoF;
    if (annoF)        params.anno = annoF;
    if (statiF.length) params.stati = statiF.join(',');
    api.getScadenzeRaggruppate(params).then(setGruppi).finally(() => setLoading(false));
  }, [q, tipoF, annoF, statiF]);

  useEffect(() => { load(); }, [load]);

  // Toggle stato nel filtro multiplo
  const toggleStato = (val) => {
    setStatiF(prev => prev.includes(val) ? prev.filter(s => s!==val) : [...prev, val]);
  };

  // Stats globali
  const totClienti  = gruppi.reduce((s,g) => s+g.n_clienti,  0);
  const totInviata  = gruppi.reduce((s,g) => s+g.n_inviata,  0);
  const totAperte   = gruppi.reduce((s,g) => s+g.n_aperte,   0);
  const totPrep     = gruppi.reduce((s,g) => s+(g.n_preparata||0), 0);

  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      const result = await api.createScadenza(formData);
      addToast(result.bulk ? `✅ Create ${result.inserite} scadenze` : 'Scadenza creata', 'success');
      setShowForm(false); load();
    } catch(e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleStato = async (scadenzaId, stato) => {
    try {
      await api.patchStato(scadenzaId, stato);
      setGruppi(prev => prev.map(g => ({
        ...g,
        clienti: g.clienti.map(c => c.id===scadenzaId ? {...c, stato} : c),
        n_inviata:     g.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='INVIATA').length,
        n_preparata:   g.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='PREPARATA').length,
        n_aperte:      g.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='APERTA').length,
        n_sospesa:     g.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='SOSPESA').length,
        n_non_prevista:g.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='NON_PREVISTA').length,
        n_altro:       g.clienti.filter(c=>(c.id===scadenzaId?stato:c.stato)==='ALTRO').length,
      })));
    } catch(e) { addToast(e.message, 'error'); }
  };

  const handleEditCliente = async ({ stato, importo, note }) => {
    setSaving(true);
    try {
      const scad = await api.getScadenza(editCliente.id);
      await api.updateScadenza(editCliente.id, { ...scad, stato, importo, note });
      addToast('Aggiornato', 'success');
      setEditCliente(null); load();
    } catch(e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll   = () => setSelected(new Set(clientiFiltrati.map(c => c.id)));
  const clearSelect = () => setSelected(new Set());

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Eliminare ${selected.size} posizioni selezionate?`)) return;
    try {
      await Promise.all([...selected].map(id => api.deleteScadenza(id)));
      addToast(`${selected.size} posizioni eliminate`, 'success');
      setSelected(new Set()); load();
    } catch(e) { addToast(e.message, 'error'); }
  };

  const handleDelete = async () => {
    try {
      await api.deleteScadenza(delItem.id);
      addToast('Eliminato', 'success');
      setDelItem(null); load();
    } catch(e) { addToast(e.message, 'error'); }
  };

  const handleDeleteGruppo = async () => {
    if (!delGruppo) return;
    try {
      const ids = delGruppo.ids ? delGruppo.ids.split(',') : [];
      await Promise.all(ids.map(id => api.deleteScadenza(id)));
      addToast(`Scadenza eliminata (${ids.length} posizioni)`, 'success');
      setDelGruppo(null); load();
    } catch(e) { addToast(e.message, 'error'); }
  };

  return (
    <div className="page-content">
      {/* Header barra azioni + filtri */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:12 }}>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuova Scadenza</button>
        <button className="btn" onClick={() => printScadenze(gruppi)}>🖨 Stampa</button>
        <button className="btn" onClick={() => exportExcel(gruppi)}>📊 Excel tutto</button>
        <div style={{ flex:1 }} />
        <select className="form-control" style={{ width:'auto' }} value={tipoF} onChange={e => setTipoF(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {TIPI.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-control" style={{ width:'auto' }} value={annoF} onChange={e => setAnnoF(e.target.value)}>
          <option value="">Tutti gli anni</option>
        </select>
        {(tipoF || annoF) && (
          <button className="btn btn-sm" style={{ color:'var(--c-red)', borderColor:'var(--c-red)' }}
            onClick={() => { setTipoF(''); setAnnoF(''); }}>
            ✕
          </button>
        )}
      </div>

      {/* Tabella scadenze */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
        {/* Header tabella */}
        <div style={{
          display:'grid', gridTemplateColumns:'100px 210px 58px 58px 58px 58px 58px 1fr',
          gap:6, padding:'8px 12px', background:'var(--bg)',
          borderBottom:'2px solid var(--border)',
          fontSize:11, fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.4px',
        }}>
          <div>Data</div>
          <div>Scadenza</div>
          <div style={{ textAlign:'center', color:'var(--c-red)' }}>Aperte</div>
          <div style={{ textAlign:'center', color:'var(--c-amber)' }}>Prep.</div>
          <div style={{ textAlign:'center', color:'var(--c-green)' }}>Inviate</div>
          <div style={{ textAlign:'center', color:'var(--text-faint)' }}>Sosp.</div>
          <div style={{ textAlign:'center', color:'var(--text-faint)' }}>N.Prev.</div>
          <div style={{ textAlign:'center' }}>Progresso</div>
        </div>
        {loading ? <div style={{ padding:32 }}><LoadingSpinner /></div>
        : gruppi.length === 0 ? (
          <div className="empty-state" style={{ padding:32 }}>
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-title">Nessuna scadenza trovata</div>
          </div>
        ) : (() => {
          const oggi = new Date(); oggi.setHours(0,0,0,0);
          const future  = gruppi.filter(g => !g.data_scad || new Date(g.data_scad) >= oggi)
                                .sort((a,b) => (a.data_scad||'').localeCompare(b.data_scad||''));
          const passate = gruppi.filter(g => g.data_scad && new Date(g.data_scad) < oggi)
                                .sort((a,b) => b.data_scad.localeCompare(a.data_scad));
          return (
            <>
              {future.map((g, i) => (
                <ScadenzaRow key={`${g.tipo}-${g.descrizione}-${g.data_scad}-${i}`} gruppo={g} navigate={navigate} onDelete={setDelGruppo} />
              ))}
              {passate.length > 0 && (
                <>
                  <div style={{ display:'flex', alignItems:'center', gap:12, padding:'7px 16px',
                    background:'var(--bg)', borderTop:'2px solid var(--border-strong)', borderBottom:'1px solid var(--border)' }}>
                    <span style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                      ⏰ Scadenze passate ({passate.length})
                    </span>
                  </div>
                  {passate.map((g, i) => (
                    <ScadenzaRow key={`past-${g.tipo}-${g.descrizione}-${g.data_scad}-${i}`} gruppo={g} navigate={navigate} past onDelete={setDelGruppo} />
                  ))}
                </>
              )}
            </>
          );
        })()}
      </div>

      {showForm && (
        <Modal title="Nuova Scadenza" onClose={() => setShowForm(false)}>
          <ScadenzaForm clientiList={clienti} onSave={handleCreate} onClose={() => setShowForm(false)} loading={saving} />
        </Modal>
      )}
      {editCliente && (
        <Modal title="Modifica stato cliente" onClose={() => setEditCliente(null)}>
          <EditClienteForm scadenza={editCliente} onSave={handleEditCliente} onClose={() => setEditCliente(null)} loading={saving} />
        </Modal>
      )}
      {delItem && (
        <ConfirmDialog message={`Eliminare la scadenza di "${clienteNome(delItem)}"?`}
          onConfirm={handleDelete} onCancel={() => setDelItem(null)} />
      )}
      {showNuovoCliente && (
        <Modal title="Aggiungi nuovo cliente" onClose={() => setShowNuovoCliente(false)}>
          <NuovoClienteScadenzaForm
            onSave={handleNuovoCliente}
            onClose={() => setShowNuovoCliente(false)}
            loading={savingNuovo} />
        </Modal>
      )}
      {delGruppo && (
        <ConfirmDigit titolo={delGruppo.descrizione||delGruppo.tipo} n_clienti={delGruppo.n_clienti}
          onConfirm={handleDeleteGruppo} onCancel={() => setDelGruppo(null)} />
      )}
    </div>
  );
}
